// 배럴(`@/api`)이 아니라 소유 모듈에서 import 한다. 배럴 소비자 수는 줄이는 방향으로 고정되어
// 있다(`scripts/architecture-baseline.json`).
import { fetchAnnotationFrameBlob, type AnnotationRequestOptions } from "@/api/trainingAnnotation";
import type { IdbCache, IdbReadOp, IdbStoreSchema, IdbWriteOp } from "@/shared/storage/idb/public";

/**
 * 프레임 PNG 바이트의 영속 캐시.
 *
 * Owns:
 * - `(uid, dataset, camera, episode, frameIndex)` 단위 적중 판정과 저장.
 * - 총 바이트 상한과 LRU 축출. 무엇을 버릴지의 정책은 이 모듈이 가진다. 커널은 op 만 제공한다.
 * - 바이트를 얼마나 오래 신뢰할지(TTL).
 *
 * Does not own:
 * - 트랜잭션·연결·degrade(`shared/storage/idb`).
 * - 프레임을 언제 받을지(c6 스튜디오와 캡처 드라이버).
 * - objectURL 수명(`driver/annotationFrameDescriptor`).
 *
 * 서버는 `frame.png` 한 장마다 비디오 디코더를 실행해 만든다. 인증 헤더가 필요해서 `<img src>` 로
 * 직접 연결할 수 없고, 브라우저 HTTP 캐시도 적중하지 않는다. c6 의 인메모리 캐시는 24장 LRU 다.
 * 그래서 세트를 바꾸거나 창을 닫으면 같은 픽셀을 처음부터 다시 받는다. 이 캐시는 그 반복을 없앤다.
 *
 * **바이트에 revision 이 없다.** 응답에 ETag 도 버전도 없다. 같은 `frame_index` 라도 상류
 * 디코더나 원본 mp4 가 바뀌면 SAM 이 보는 픽셀과 다를 수 있다. 하지만 그것을 알릴 필드가 없다.
 * 그래서 바이트를 불변으로 단정하지 않고 TTL 로 수명을 제한한다. 설정의 수동 비우기도 해결
 * 방법으로 남긴다.
 */

const FRAME_STORE = "annotationFrames";
const ENTRY_STORE = "annotationFrameEntries";

// 바이트와 장부를 나눈 이유: 회수 판정은 모든 항목을 봐야 한다. 한 스토어에 두면 그 조회가 모든
// Blob 을 메모리에 올린다. 장부에는 크기와 접근 시각만 둔다.
const FRAME_KEY_PATH = ["uid", "datasetUuid", "camera", "episode", "frameIndex"] as const;

/** 이 캐시가 요구하는 스토어. 데이터베이스 구성은 앱(`app/hatcheryDatabase`)이 한다. */
export const annotationFrameStores: readonly IdbStoreSchema[] = [
  { name: FRAME_STORE, keyPath: FRAME_KEY_PATH },
  { name: ENTRY_STORE, keyPath: FRAME_KEY_PATH },
];

/**
 * 총 바이트 상한. 카메라 프레임 PNG 는 장당 수백 KB~수 MB 이므로 수백 장을 담는 크기다. 한 번의
 * 어노테이션 작업이 접근하는 범위를 담으면서, 디스크를 알림 없이 모두 차지하지 않는 값으로 골랐다.
 * 사용자는 설정에서 현재 사용량을 보고 직접 비울 수 있다.
 */
const DEFAULT_MAX_BYTES = 512 * 1024 * 1024;

/** 바이트를 신뢰하는 기간. revision 이 없어 상류 변경을 알 수 없으므로 수명으로 제한한다. */
const DEFAULT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

type FetchFrame = (
  datasetUuid: string,
  camera: string,
  episode: string,
  frameIndex: number,
  options?: AnnotationRequestOptions,
) => Promise<Blob>;

type FrameKey = {
  uid: string;
  datasetUuid: string;
  camera: string;
  episode: string;
  frameIndex: number;
};

type FrameEntry = FrameKey & {
  sizeBytes: number;
  storedAtMs: number;
  lastAccessAtMs: number;
};

export type AnnotationFrameBytesCacheOptions = {
  cache: IdbCache;
  // 현재 세션의 사용자. 저장소는 origin 단위라 키로 한 번 더 구분한다. 그래서 로그아웃 시 삭제가
  // 실패해도 다음 사용자가 다른 사용자의 바이트를 읽지 않는다. 데이터베이스의 수명을 정하는 곳과
  // 같은 곳에서 읽어야 두 값이 어긋나지 않는다. 그래서 값이 아니라 조회 함수를 받는다.
  getUid: () => string | null;
  fetchFrame?: FetchFrame;
  maxBytes?: number;
  maxAgeMs?: number;
  now?: () => number;
};

export class AnnotationFrameBytesCache {
  private readonly cache: IdbCache;
  private readonly getUid: () => string | null;
  private readonly fetchFrame: FetchFrame;
  private readonly maxBytes: number;
  private readonly maxAgeMs: number;
  private readonly now: () => number;

  constructor(options: AnnotationFrameBytesCacheOptions) {
    this.cache = options.cache;
    this.getUid = options.getUid;
    this.fetchFrame = options.fetchFrame ?? fetchAnnotationFrameBlob;
    this.maxBytes = options.maxBytes ?? DEFAULT_MAX_BYTES;
    this.maxAgeMs = options.maxAgeMs ?? DEFAULT_MAX_AGE_MS;
    this.now = options.now ?? Date.now;
  }

  /**
   * 프레임 바이트를 반환한다. 적중이면 저장소에서 읽고, 아니면 상류에서 받아 저장한다.
   *
   * 상류 호출의 `options` 는 그대로 전달한다. 취소한 호출자가 자기 요청을 끊을 수 있어야 한다.
   * 저장 실패는 무시한다. 캐시가 동작하지 않는 것은 캡처의 실패가 아니다.
   */
  get: FetchFrame = async (datasetUuid, camera, episode, frameIndex, options) => {
    const key: FrameKey = {
      uid: this.getUid() ?? "anonymous",
      datasetUuid,
      camera,
      episode,
      frameIndex,
    };
    const hit = await this.readHit(key);
    if (hit !== null) {
      return hit;
    }
    const blob = await this.fetchFrame(datasetUuid, camera, episode, frameIndex, options);
    await this.store(key, blob);
    return blob;
  };

  private async readHit(key: FrameKey): Promise<Blob | null> {
    // 두 스토어를 한 스냅샷으로 읽는다. 그래서 다른 창이 교체·삭제한 바이트와 이전 장부를 섞지 않는다.
    const values = await this.cache.readSnapshot(frameReads(key));
    if (values === null) {
      return null;
    }
    const entry = acceptEntry(values[0]);
    const stored = acceptFrameRecord(values[1]);
    const nowMs = this.now();
    if (values[0] !== undefined || values[1] !== undefined) {
      void this.refreshEntry(key, nowMs);
    }
    return entry !== null && stored !== null && nowMs - entry.storedAtMs <= this.maxAgeMs
      ? stored.blob
      : null;
  }

  private async refreshEntry(key: FrameKey, nowMs: number): Promise<void> {
    // 조회 뒤 다른 창이 항목을 지우거나 교체했을 수 있다. 쓰기 잠금 안에서 다시 읽는다. 그래서 지운
    // 행을 되살리지 않고, 새 바이트를 지우지 않고, 최신 접근 시각을 이전 값으로 되돌리지 않는다.
    await this.cache.readModifyWrite({
      stores: [FRAME_STORE, ENTRY_STORE],
      reads: frameReads(key),
      plan: ([rawEntry, rawFrame]) => {
        const entry = acceptEntry(rawEntry);
        if (
          entry === null ||
          acceptFrameRecord(rawFrame) === null ||
          nowMs - entry.storedAtMs > this.maxAgeMs
        ) {
          return removeOps([key]);
        }
        return [
          {
            kind: "put",
            store: ENTRY_STORE,
            values: [{ ...entry, lastAccessAtMs: Math.max(entry.lastAccessAtMs, nowMs) }],
          },
        ];
      },
    });
  }

  private async store(key: FrameKey, blob: Blob): Promise<void> {
    if (blob.size > this.maxBytes) return;
    await this.cache.readModifyWrite({
      stores: [FRAME_STORE, ENTRY_STORE],
      // 장부는 매번 DB에서 읽는다. 다른 창의 저장과 접근도 회수 판단에 반영해야 한다.
      reads: [{ kind: "getAll", store: ENTRY_STORE }],
      plan: ([raw]) => {
        const rows = Array.isArray(raw) ? raw.map(acceptEntry) : null;
        if (rows === null || !rows.every((row): row is FrameEntry => row !== null)) {
          // 장부만 지우면 남은 Blob 을 다음 창이 집계할 수 없다. 두 스토어를 함께 삭제한다.
          return [
            { kind: "clear", store: FRAME_STORE },
            { kind: "clear", store: ENTRY_STORE },
          ];
        }
        const entries = new Map(rows.map((row) => [entryId(row), row]));
        const nowMs = this.now();
        const entry: FrameEntry = {
          ...key,
          sizeBytes: blob.size,
          storedAtMs: nowMs,
          lastAccessAtMs: nowMs,
        };
        const victims = this.pickVictims(entries, entryId(key), blob.size);
        return [
          ...removeOps(victims),
          { kind: "put", store: FRAME_STORE, values: [{ ...key, blob }] },
          { kind: "put", store: ENTRY_STORE, values: [entry] },
        ];
      },
    });
  }

  /** 새 항목이 들어갈 공간을 만든다. 상한을 넘는 만큼 가장 오래 쓰지 않은 항목부터 고른다. */
  private pickVictims(
    entries: ReadonlyMap<string, FrameEntry>,
    incomingId: string,
    incomingBytes: number,
  ): FrameKey[] {
    let total = incomingBytes;
    for (const [id, entry] of entries) {
      // 같은 키를 덮어쓰면 이전 바이트는 어차피 사라진다.
      if (id !== incomingId) {
        total += entry.sizeBytes;
      }
    }
    if (total <= this.maxBytes) {
      return [];
    }
    const oldestFirst = [...entries.values()]
      .filter((entry) => entryId(entry) !== incomingId)
      .sort((left, right) => left.lastAccessAtMs - right.lastAccessAtMs);
    const victims: FrameKey[] = [];
    for (const entry of oldestFirst) {
      if (total <= this.maxBytes) {
        break;
      }
      total -= entry.sizeBytes;
      victims.push(entry);
    }
    return victims;
  }
}

function frameReads(key: FrameKey): readonly IdbReadOp[] {
  return [
    { kind: "get", store: ENTRY_STORE, key: primaryKey(key) },
    { kind: "get", store: FRAME_STORE, key: primaryKey(key) },
  ];
}

function removeOps(keys: readonly FrameKey[]): IdbWriteOp[] {
  if (keys.length === 0) {
    return [];
  }
  const primaryKeys = keys.map(primaryKey);
  return [
    { kind: "delete", store: FRAME_STORE, keys: primaryKeys },
    { kind: "delete", store: ENTRY_STORE, keys: primaryKeys },
  ];
}

function primaryKey(key: FrameKey): IDBValidKey {
  return [key.uid, key.datasetUuid, key.camera, key.episode, key.frameIndex];
}

function entryId(key: FrameKey): string {
  return JSON.stringify(primaryKey(key));
}

function acceptFrameRecord(raw: unknown): { blob: Blob } | null {
  if (typeof raw !== "object" || raw === null) {
    return null;
  }
  const blob = (raw as { blob?: unknown }).blob;
  return blob instanceof Blob ? { blob } : null;
}

function acceptEntry(raw: unknown): FrameEntry | null {
  if (typeof raw !== "object" || raw === null) {
    return null;
  }
  const { uid, datasetUuid, camera, episode, frameIndex, sizeBytes, storedAtMs, lastAccessAtMs } =
    raw as Partial<FrameEntry>;
  if (
    typeof uid !== "string" ||
    typeof datasetUuid !== "string" ||
    typeof camera !== "string" ||
    typeof episode !== "string" ||
    !isFiniteNumber(frameIndex) ||
    !isFiniteNumber(sizeBytes) ||
    !isFiniteNumber(storedAtMs) ||
    !isFiniteNumber(lastAccessAtMs)
  ) {
    return null;
  }
  return {
    uid,
    datasetUuid,
    camera,
    episode,
    frameIndex,
    sizeBytes,
    storedAtMs,
    lastAccessAtMs,
  };
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}
